import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { JOURNEY_RELEVANCE } from "contracts/enums/issue-semantics";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import { METRIC_KEY } from "contracts/report/catalog";
import { contentFitnessSubcriterionForKey } from "contracts/report/content-fitness/lookups";
import { JOURNEY_FRICTION_KIND } from "../../enums/scan";
import { clientDisplayText } from "./evidencePresentation";
import { journeyFact } from "./issueSemantics";
import { clientIssueTitle, suggestedActionForIssueInSite } from "./issueText";
import type { CandidateJourney, JourneyFriction } from "./journeyTypes";

export function journeyIssueTitle(issue: Issue): string {
  return clientDisplayText(clientIssueTitle(issue));
}

export function buildAllJourneyFrictions(
  data: OverviewResponse,
  path: CandidateJourney,
  outgoing: Map<string, OverviewResponse["links"]>,
): JourneyFriction[] {
  const frictions: JourneyFriction[] = path.links.flatMap((link) =>
    link.agentBroken
      ? [
          {
            key: `broken-${link.fromPageId}-${link.toPageId}`,
            kind: "broken_link" as const,
            label: "Potential path break",
            detail:
              link.brokenReason ??
              "The scan recorded this structural link as broken.",
            page:
              path.steps.find((step) => step.page.id === link.fromPageId)
                ?.page ?? null,
            issue: null,
          },
        ]
      : [],
  );
  for (const step of path.steps) {
    for (const issue of step.issues.filter(isJourneyIssue)) {
      frictions.push({
        key: `issue-${issue.id}-${step.page.id}`,
        kind: JOURNEY_FRICTION_KIND.JOURNEY_ISSUE,
        label: issueFrictionLabel(issue),
        detail: journeyIssueTitle(issue),
        page: step.page,
        issue,
      });
    }
  }
  const finalPage = path.steps.at(-1)?.page;
  if (data.links.length > 0 && finalPage && !outgoing.has(finalPage.id)) {
    frictions.push({
      key: `dead-end-${finalPage.id}`,
      kind: JOURNEY_FRICTION_KIND.NO_ONWARD_LINK,
      label: "No recorded onward link",
      detail:
        "No outgoing internal link from this page was recorded in the current scan.",
      page: finalPage,
      issue: null,
    });
  }
  if (path.assessedPages < path.steps.length) {
    frictions.push({
      key: `coverage-${path.id}`,
      kind: JOURNEY_FRICTION_KIND.PARTIAL_COVERAGE,
      label: "Path not fully assessed",
      detail: `${path.assessedPages} of ${path.steps.length} pages have assessment data.`,
      page: null,
      issue: null,
    });
  }
  return [...new Map(frictions.map((row) => [row.key, row])).values()].sort(
    compareJourneyFrictions,
  );
}

export function journeyFrictionNextStep(
  data: OverviewResponse,
  friction: JourneyFriction,
): string {
  return friction.issue
    ? clientDisplayText(suggestedActionForIssueInSite(data, friction.issue))
    : deterministicNextStep(friction);
}

function isJourneyIssue(issue: Issue): boolean {
  const subcriterion = issue.primarySubcriterionKey
    ? contentFitnessSubcriterionForKey(issue.primarySubcriterionKey)
    : null;
  return (
    issue.metricKey === METRIC_KEY.JOURNEY_USABILITY_ACTION ||
    subcriterion?.parentMetricKey === METRIC_KEY.JOURNEY_USABILITY_ACTION ||
    journeyFact(issue) !== null
  );
}

function issueFrictionLabel(issue: Issue): string {
  return journeyFact(issue)?.relevance === JOURNEY_RELEVANCE.ONWARD_STEP
    ? "Unclear onward step"
    : "Related navigation issue";
}

function deterministicNextStep(friction: JourneyFriction): string {
  if (friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK) {
    return "Inspect and repair the recorded link.";
  }
  if (friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK) {
    return "Review whether this page needs a clear onward route.";
  }
  return "Assess the unreviewed page before treating this sequence as complete.";
}

function compareJourneyFrictions(
  left: JourneyFriction,
  right: JourneyFriction,
): number {
  return (
    frictionOrder(left) - frictionOrder(right) ||
    left.key.localeCompare(right.key)
  );
}

function frictionOrder(friction: JourneyFriction): number {
  if (friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK) return 0;
  if (friction.issue?.severity === ISSUE_SEVERITY.CRITICAL) return 1;
  if (friction.issue?.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH)
    return 2;
  if (friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK) return 3;
  if (friction.issue?.severity === ISSUE_SEVERITY.MEDIUM) return 4;
  if (friction.issue) return 5;
  return 6;
}
